import { API, graphqlOperation } from "aws-amplify";
import {
  railEquipmentTypeByActiveBySortKey,
  truckEquipmentTypeByActiveBySortKey,
} from "../graphql/queries";

import {
  LocationLoadType,
  UnitConversion,
  UnitMap,
  UnitToColumn,
  Unit,
  UnitWeightOptions,
  UnitOptions,
  InventoryFieldOptions,
  InventoryField,
} from "./constants";
import { isBulkArea } from "./locationUtils";
import { trimDecimal } from "./utils";

const getCommodity = (commodity) => {
  if (!commodity) return null;
  if (typeof commodity === "object") {
    // expecting commodity or reloadYardCommodity
    return commodity.commodity || commodity;
  } else if (typeof commodity === "string") {
    // expecting reloadYardCommodityId
    const commodityObj = getYardCommodity(commodity)?.commodity;
    return commodityObj;
  }
};

export const getYardCommodity = (yardCommodityId) => {
  if (!yardCommodityId) return null;
  const yardCommodity =
    window.appUser?.reloadYard?.yardCommodityMap[yardCommodityId];
  return yardCommodity;
};

export const isLumberCommodity = (commodity) => {
  let loadType = null;
  if (typeof commodity === "object") {
    // expecting commodity or reloadYardCommodity
    loadType = commodity?.name || commodity?.commodity?.name;
  } else if (typeof commodity === "string") {
    // expecting reloadYardCommodityId
    loadType =
      window.appUser?.reloadYard?.yardCommodityMap[commodity]?.commodity?.name;
  }

  return loadType === "Lumber";
};

export const isPanelsCommodity = (commodity) => {
  let loadType = null;
  if (typeof commodity === "object") {
    // expecting commodity or reloadYardCommodity
    loadType = commodity?.name || commodity?.commodity?.name;
  } else if (typeof commodity === "string") {
    // expecting reloadYardCommodityId
    loadType =
      window.appUser?.reloadYard?.yardCommodityMap[commodity]?.commodity?.name;
  }

  return loadType === "Panels";
};

export const isEngineeredLumberCommodity = (commodity) => {
  let loadType = null;
  if (typeof commodity === "object") {
    // expecting commodity or reloadYardCommodity
    loadType = commodity?.name || commodity?.commodity?.name;
  } else if (typeof commodity === "string") {
    // expecting reloadYardCommodityId
    loadType =
      window.appUser?.reloadYard?.yardCommodityMap[commodity]?.commodity?.name;
  }

  return loadType === "Engineered Lumber";
};

export const isBulkCommodity = (commodity) => {
  let loadType = null;
  if (typeof commodity === "object") {
    // expecting commodity or reloadYardCommodity
    loadType =
      commodity?.locationLoadType || commodity?.commodity?.locationLoadType;
  } else if (typeof commodity === "string") {
    // expecting reloadYardCommodityId
    loadType =
      window.appUser?.reloadYard?.yardCommodityMap[commodity]?.commodity
        ?.locationLoadType;
  }

  return (
    loadType === LocationLoadType.DRY_BULK ||
    loadType === LocationLoadType.LIQUID_BULK
  );
};

export const isLiquidBulkCommodity = (commodity) => {
  let loadType = null;
  if (typeof commodity === "object") {
    // expecting commodity or reloadYardCommodity
    loadType =
      commodity?.locationLoadType || commodity?.commodity?.locationLoadType;
  } else if (typeof commodity === "string") {
    // expecting reloadYardCommodityId
    loadType =
      window.appUser?.reloadYard?.yardCommodityMap[commodity]?.commodity
        ?.locationLoadType;
  }

  return loadType === LocationLoadType.LIQUID_BULK;
};

export const isBulkShipment = (commodity, area) => {
  return isBulkCommodity(commodity) || isBulkArea(area);
};

export const isShippingContainer = (commodity) => {
  let loadType = null;

  if (typeof commodity === "object") {
    // expecting commodity
    loadType = commodity?.locationLoadType;
  } else if (typeof commodity === "string") {
    // expecting reloadYardCommodityId
    loadType =
      window.appUser?.reloadYard?.yardCommodityMap[commodity]?.commodity
        ?.locationLoadType;
  }

  return loadType === LocationLoadType.CONTAINER;
};

export const getDefaultUOM = (yardCommodityId) => {
  const yardCommodity = getYardCommodity(yardCommodityId);
  return yardCommodity.units;
};

export const isWeightUOM = (uom) => {
  if (!uom) return false;
  return UnitWeightOptions.some((option) => option.id === uom);
};

/** Resolves weight UOM from inventory row, product, or account defaults. */
export const getWeightUOM = (account, scopedFormData) => {
  const product = scopedFormData?.product;
  const inventory = scopedFormData;
  if (inventory?.weightUOM) return inventory?.weightUOM;
  if (product?.defaultWeightUOM) return product.defaultWeightUOM;
  if (account?.defaultWeightUOM) return account.defaultWeightUOM;
  return null;
};

// used for lumber/panels/engineered invoicing when no product exists
export const convertLumberUnits = (inventory, quantity, toUnits) => {
  const commodity = inventory.reloadYardCommodity?.commodity?.name
    ? inventory.reloadYardCommodity?.commodity
    : inventory.reloadYardCommodityId;
  let result = null;

  if (isLumberCommodity(commodity) && toUnits === Unit.MBF) {
    const mbfPerUnit = calculateMbf(inventory);
    result = mbfPerUnit * quantity;
  } else if (isPanelsCommodity(commodity) && toUnits === Unit.MSF) {
    const msfPerUnit = calculateMsf(inventory);
    result = msfPerUnit * quantity;
  } else if (isEngineeredLumberCommodity(commodity) && toUnits === Unit.LF) {
    const lfPerUnit = calculateLf(inventory);
    result = lfPerUnit * quantity;
  }

  if (result == null || isNaN(result)) return { error: true, value: null };
  return { error: false, value: trimDecimal(result, 3) };
};

export const getConversionValue = (
  inventory,
  quantity,
  toUnits,
  fromUnits = inventory?.inventoryUOM,
  fallback = null,
) => {
  if (quantity == null || !toUnits || !fromUnits || !inventory) return fallback;
  if (fromUnits === toUnits) return quantity;

  const inventoryForConversion =
    inventory?.inventoryUOM === fromUnits
      ? inventory
      : { ...inventory, inventoryUOM: fromUnits };

  const lumberConversion = convertLumberUnits(
    inventoryForConversion,
    quantity,
    toUnits,
  );
  if (!lumberConversion?.error) return lumberConversion.value;

  const productConversion = convertUnits(
    quantity,
    fromUnits,
    toUnits,
    inventory?.product || null,
  );
  return productConversion?.error ? fallback : productConversion.value;
};

export const getUnitConversions = ({
  inventory,
  quantity,
  fromUnits,
  commodity,
  product,
}) => {
  if (!inventory) return [];

  const conversionCommodity =
    commodity ||
    inventory?.reloadYardCommodityId ||
    inventory?.reloadYardCommodity?.commodity;
  const conversionProduct = product || inventory?.product || null;
  const infoBubble = [];

  Object.keys(Unit).forEach((key) => {
    if (key === fromUnits) return;

    const inventoryForConversion = {
      ...inventory,
      product: conversionProduct,
      reloadYardCommodityId:
        inventory?.reloadYardCommodityId || conversionCommodity || null,
    };
    const billRate = getConversionValue(
      inventoryForConversion,
      quantity,
      key,
      fromUnits,
    );

    if (billRate != null) {
      infoBubble.push({ unit: key, value: billRate });
    }
  });

  return infoBubble;
};

export const convertUnits = (fromQty, fromUnits, toUnits, product) => {
  let fromRate = null;
  let toRate = null;

  if (fromUnits !== toUnits) {
    if (product) {
      // product-based conversion
      fromRate = product[UnitToColumn[fromUnits || product.defaultUOM]];
      toRate = product[UnitToColumn[toUnits]];
    }
    if (!fromRate || !toRate) {
      // metric & emperial conversion
      const converstion = UnitConversion[fromUnits];
      fromRate = converstion?.[fromUnits];
      toRate = converstion?.[toUnits];
    }

    if (
      fromRate !== null &&
      !isNaN(fromRate) &&
      toRate !== null &&
      !isNaN(toRate)
    ) {
      if (Number(fromQty) !== 0) {
        const base = fromRate / fromQty;
        if (base) return { error: false, value: trimDecimal(toRate / base, 3) };
      } else return { error: false, value: 0 };
    } else return { error: true, value: null };
  } else return { error: false, value: fromQty };
};

export const getUnitLabel = (units) => {
  if (units) return `${UnitMap[units].name}: ${UnitMap[units].description}`;
  else return "";
};

export const getUnitName = (units) => {
  if (units) {
    const unitName = UnitMap[units]?.name;
    if (unitName) return unitName;
  }
  return "";
};

export const getUnit = (column) => {
  return Object.keys(UnitToColumn).find((key) => UnitToColumn[key] === column);
};

export const getSquaredAreaUOM = () => {
  const areaUOMOptions = UnitOptions.filter(({ id }) =>
    [Unit.SF, Unit.MSF, Unit.SQM].includes(id),
  );
  return areaUOMOptions;
};

export const getCommodityUnits = (commodity) => {
  if (!commodity) return UnitOptions;

  // accpets a commodity object, or reloadYardCommodityId if commodity not available
  const commodityObject = getCommodity(commodity);
  // Check if validUom exists and is not an empty array, else return all UnitOptions
  if (!commodityObject?.validUom || commodityObject?.validUom.length === 0) {
    return [];
  }

  // Filter UnitOptions to only include units that are present in commodity.validUom
  return UnitOptions.filter((unitOption) =>
    commodityObject.validUom.includes(unitOption.id),
  );
};

export const getInventoryFields = (commodity) => {
  // accpets a commodity object, or reloadYardCommodityId if commodity not available
  const commodityObject = getCommodity(commodity);
  // Check if validUom exists and is not an empty array, else return all UnitOptions
  if (
    !commodityObject?.inventoryFields ||
    commodityObject?.inventoryFields.length === 0
  ) {
    return [];
  }

  // Filter UnitOptions to only include units that are present in commodity.validUom
  return InventoryFieldOptions.filter((field) =>
    commodityObject.inventoryFields.includes(field.id),
  );
};

export const isInventoryFieldVisible = (field, commodity) => {
  if (!field || !commodity) return false;

  const inventoryFields = getInventoryFields(commodity);
  return inventoryFields.some((item) => item.id === field);
};

export const isPieceDimensionsVisible = (commodity) => {
  if (!commodity) return false;

  const inventoryFields = getInventoryFields(commodity);
  return inventoryFields.some(
    (item) =>
      item.id === InventoryField.HEIGHT ||
      item.id === InventoryField.WIDTH ||
      item.id === InventoryField.LENGTH,
  );
};

export const isQuantityInUnits = (currentInventory) => {
  return currentInventory?.inventoryUOM === Unit.UNIT;
};

export const unitOptionRenderer = (record) =>
  `${record.name} - ${record.description}`;

export const unitNameRenderer = (record) => {
  return getUnitName(record.units);
};

/**
 * PPU / pieces per unit for an inventory row or nested `inventory` on a line item.
 * Prefer `pcs` on the inventory record; if null/undefined, use linked `product.pcs`.
 * (`??` preserves inventory `0`.)
 */
export const getPPU = (record) => record?.pcs ?? record?.product?.pcs;

/**
 * Calculate MBF per UNIT
 * Assumes:
 *   - height is in inches
 *   - width is in inches
 *   - length is in feet
 *
 * @param {Object} inventory - Inventory record containing dimensions and quantity.
 * @returns {number|null} Total board footage, or null if data is incomplete.
 */
export const calculateMbf = (inventory) => {
  if (!inventory) return null;
  const height = inventory.pcsHeight || inventory.product?.pcsHeight;
  const width = inventory.pcsWidth || inventory.product?.pcsWidth;
  const length = inventory.pcsLength || inventory.product?.pcsLength;
  const pcs = inventory.pcs || inventory.product?.pcs;

  // Check if any of the variables are null or undefined
  if (height == null || width == null || length == null || pcs == null) {
    return null;
  }

  const mbf = trimDecimal((height * width * length * pcs) / 12 / 1000, 3);
  return mbf;
};

/**
 * Calculate SF per UNIT
 * Assumes:
 *   - width is in inches
 *   - length is in feet
 *
 * @param {Object} inventory - Inventory record containing dimensions and quantity.
 * @returns {number|null} Total square footage, or null if data is incomplete.
 */
export const calculateSqf = (inventory) => {
  if (!inventory) return null;

  const width = inventory.pcsWidth ?? inventory.product?.pcsWidth;
  const length = inventory.pcsLength ?? inventory.product?.pcsLength;
  const pcs = inventory.pcs ?? inventory.product?.pcs;

  // Validate required values
  if (width == null || length == null || pcs == null) {
    return null;
  }

  // Convert width from inches → feet, multiply by length (feet) × pieces
  const sqf = (width / 12) * length * pcs;

  return trimDecimal(sqf, 3);
};

/**
 * Calculate MSF per UNIT
 * Assumes:
 *   - width is in inches
 *   - length is in feet
 *
 * @param {Object} inventory - Inventory record containing dimensions and quantity.
 * @returns {number|null} Total thousand square footage, or null if data is incomplete.
 */
export const calculateMsf = (inventory) => {
  const sqf = calculateSqf(inventory);
  if (sqf == null) return null;

  return trimDecimal(sqf / 1000, 3);
};

/**
 * Calculate LF per UNIT
 * Assumes:
 *   - length is in feet
 *
 * @param {Object} inventory - Inventory record containing dimensions and quantity.
 * @returns {number|null} Total linear feet, or null if data is incomplete.
 */
export const calculateLf = (inventory) => {
  if (!inventory) return null;
  const length = inventory.pcsLength || inventory.product?.pcsLength;
  const pcs = inventory.pcs || inventory.product?.pcs;

  // Check if any of the variables are null or undefined
  if (length == null || pcs == null) {
    return null;
  }

  const lf = trimDecimal(length * pcs, 3);
  return lf;
};

export const getRailEquipmentTypes = (record) => {
  const reloadYard = record || window.appUser?.reloadYard;
  const railEquipmentTypes = reloadYard?.railEquipmentTypes?.items || [];
  return railEquipmentTypes.sort((a, b) => a.sortKey - b.sortKey);
};
export const getTruckEquipmentTypes = (record) => {
  const reloadYard = record || window.appUser?.reloadYard;
  const truckEquipmentTypes = reloadYard?.truckEquipmentTypes?.items || [];
  return truckEquipmentTypes.sort((a, b) => a.sortKey - b.sortKey);
};

export const getRailEquipmentTypeNameById = (equipmentTypeId, record) => {
  if (!equipmentTypeId) return null;

  const railEquipmentTypes = getRailEquipmentTypes(record);
  const railEquipmentType = railEquipmentTypes.find(
    (equipmentType) => equipmentType?.id === equipmentTypeId,
  );
  return railEquipmentType?.name || null;
};

export const getTruckEquipmentTypeNameById = (equipmentTypeId, record) => {
  if (!equipmentTypeId) return null;

  const truckEquipmentTypes = getTruckEquipmentTypes(record);
  const truckEquipmentType = truckEquipmentTypes.find(
    (equipmentType) => equipmentType?.id === equipmentTypeId,
  );
  return truckEquipmentType?.name || null;
};

/** Resolves display name for `carrierEquipmentTypeId` (rail or truck yard equipment type). */
export const getCarrierEquipmentTypeNameById = (equipmentTypeId, record) => {
  if (!equipmentTypeId) return null;

  return (
    getRailEquipmentTypeNameById(equipmentTypeId, record) ||
    getTruckEquipmentTypeNameById(equipmentTypeId, record)
  );
};

export const fetchCarrierEquipmentTypes = async () => {
  // Rail equipment types
  const railRes = await API.graphql(
    graphqlOperation(railEquipmentTypeByActiveBySortKey, {
      reloadYardId: window.appUser.reloadYardId,
      activeSortKey: { beginsWith: { active: "true" } },
      limit: 1000,
    }),
  );
  const railEquipmentTypes =
    railRes?.data?.railEquipmentTypeByActiveBySortKey?.items ?? [];

  // Truck equipment types
  const truckRes = await API.graphql(
    graphqlOperation(truckEquipmentTypeByActiveBySortKey, {
      reloadYardId: window.appUser.reloadYardId,
      activeSortKey: { beginsWith: { active: "true" } },
      limit: 1000,
    }),
  );
  const truckEquipmentTypes =
    truckRes?.data?.truckEquipmentTypeByActiveBySortKey?.items ?? [];
  return { railEquipmentTypes, truckEquipmentTypes };
};
